import { Nullable } from '@rotomeca/utils';

type Value = string | number | boolean;
type AttrCallback = (
  name: string,
  value?: Nullable<Value>,
) => void | Nullable<string>;
type AttrsCallback = (attribs: Record<string, Value>) => void;
type MayBeBnumElement = HTMLElement & {
  attr?: Nullable<AttrCallback>;
  attrs?: Nullable<AttrsCallback>;
};

/**
 * @SetAttr : Ajoute un attribut avec une valeur fixe à un élément.
 * @param attributeName Nom de l'attribut à ajouter.
 * @param value Valeur de l'attribut à définir.
 * @returns Un décorateur de méthode qui ajoute l'attribut à l'élément.
 */
export function SetAttr(attributeName: string, value: Value) {
  return function (originalMethod: any, context: ClassMethodDecoratorContext) {
    if (context.kind !== 'method') return;

    return function (this: MayBeBnumElement, ..._args: any[]) {
      const rtn = originalMethod.apply(this, _args);
      _setAttribute(this, attributeName, value);

      return rtn;
    };
  };
}

/**
 * @SetAttrs : Ajoute un attribut avec une valeur fixe à un élément.
 * @param attributeName Nom de l'attribut à ajouter.
 * @param value Valeur de l'attribut à définir.
 * @returns Un décorateur de méthode qui ajoute l'attribut à l'élément.
 */
export function SetAttrs(attribs: Record<string, string | boolean | number>) {
  return function (originalMethod: any, context: ClassMethodDecoratorContext) {
    if (context.kind !== 'method') return;

    return function (this: MayBeBnumElement, ..._args: any[]) {
      if (this?.attrs) this.attrs(attribs);
      else {
        for (const [key, value] of Object.entries(attribs)) {
          this.setAttribute(key, value as unknown as string);
        }
      }

      return originalMethod.apply(this, _args);
    };
  };
}

/**
 * @SetAttr : Ajoute un attribut avec une valeur fixe à un élément.
 * @param attributeName Nom de l'attribut à ajouter.
 * @param value Valeur de l'attribut à définir.
 * @returns Un décorateur de méthode qui ajoute l'attribut à l'élément.
 */
export function InitAttr(attributeName: string, value: Value) {
  return function (originalMethod: any, context: ClassMethodDecoratorContext) {
    if (context.kind !== 'method') return;

    return function (this: MayBeBnumElement, ..._args: any[]) {
      const rtn = originalMethod.apply(this, _args);

      if (_getAttribute(this, attributeName) === null)
        _setAttribute(this, attributeName, value);

      return rtn;
    };
  };
}

function _setAttribute(
  instance: MayBeBnumElement,
  attributeName: string,
  value: Value,
): void {
  if (instance?.attr) instance.attr(attributeName, value);
  else instance.setAttribute(attributeName, value as unknown as string);
}

function _getAttribute(
  instance: MayBeBnumElement,
  attributeName: string,
): Nullable<string> {
  if (instance?.attr) return instance.attr(attributeName) as Nullable<string>;
  else return instance.getAttribute(attributeName);
}
